import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useEtatGlobal } from "../EtatGlobal.js";
import {
  Mic,
  MicOff,
  Trash2,
  FileText,
  Loader2,
  Save,
  UserSearch,
  Paperclip,
  Upload,
  X,
} from "lucide-react";
import Editor from "react-simple-wysiwyg";

import { authFetch } from '../../HttpClient';
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";

import { dialog } from "../../components/ui/Dialog";
const Rdv = ({ clientId, clientNom, formLeadId }) => {
  const { utilisateur } = useEtatGlobal();
  const [summary, setSummary] = useState("");
  const [summaryMail, setSummaryMail] = useState("");
  const [isLoadingMail, setIsLoadingMail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [manualText, setManualText] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");

  const [searchClient, setSearchClient] = useState(clientNom || "");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(
    clientId ? { id: clientId, nom: clientNom } : null
  );

  const [titre, setTitre] = useState("");
  const [confidentialite, setConfidentialite] = useState("Public");
  const [documentFile, setDocumentFile] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const recognitionRef = useRef(null);

  const handleResult = (event) => {
    let interim = "";
    let final = "";
    let manual = "";

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const text = result[0].transcript;

      if (result.isFinal) {
        final += text + " ";
      } else {
        interim += text;
      }
    }

    if (final) {
      setFinalTranscript((prev) => prev + final);
      setInterimTranscript("");
    }

    if (manual) {
      setManualText(manual);
    }

    if (interim) {
      setInterimTranscript(interim);
    }
  };

  const toggleListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "fr-FR";
      recognition.onresult = handleResult;
      recognition.onerror = (e) => console.error("Erreur:", e);
      recognitionRef.current = recognition;
    }

    if (!isListening) {
      recognitionRef.current.start();
    } else {
      recognitionRef.current.stop();
    }

    setIsListening(!isListening);
  };

  const clearText = () => {
    setFinalTranscript("");
    setInterimTranscript("");
    setManualText("");
  };

  // Résumé et mail : générés par le serveur (Claude), qui garde la clé de
  // l'IA ; mêmes consignes qu'avant (backend controllers/rdvIaController.js).
  const genererTexte = async (type) => {
    const res = await authFetch("https://backend-myalfa.vercel.app/api/ia-rdv/note", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, transcription: finalTranscript, notes: manualText }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || "Le texte n'a pas pu être généré. Réessayez.");
    return data.texte || "";
  };

  const summarizeText = async () => {
    setIsLoading(true);
    setSummary("");

    try {
      setSummary(await genererTexte("resume"));
    } catch (error) {
      console.error("Erreur résumé :", error);
      dialog.alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const summarizeMail = async () => {
    setIsLoadingMail(true);
    setSummaryMail("");

    try {
      setSummaryMail(await genererTexte("mail"));
    } catch (error) {
      console.error("Erreur mail :", error);
      dialog.alert(error.message);
    } finally {
      setIsLoadingMail(false);
    }
  };

  const handleSearchClient = async (e) => {
    const query = e.target.value;
    setSearchClient(query);

    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/clients/search?search=${encodeURIComponent(
          query
        )}`
      );
      const data = await res.json();
      setClientSuggestions(data);
    } catch (error) {
      console.error("Erreur recherche client :", error);
      setClientSuggestions([]);
    }
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setSearchClient(client.nom);
    setClientSuggestions([]);
  };

  useEffect(() => {
    if (clientId && clientNom) {
      setSelectedClient({ id: clientId, nom: clientNom });
      setSearchClient(clientNom);
    }
  }, [clientId, clientNom]);

  console.log("Client ID sélectionné :", selectedClient?.id);
  console.log("Lead ID sélectionné :", formLeadId);

  const enregistrerNote = async () => {
    if (!selectedClient && !formLeadId) {
      dialog.alert("Veuillez séléctionner un Client ou Prospect.");
      return;
    }

    setIsSaving(true);

    try {
      // FormData, pas JSON : le document joint (optionnel) doit voyager en
      // multipart. Le backend (voir noteRdvController.js) attend ce format
      // sur cette route desormais, avec ou sans fichier.
      const formData = new FormData();
      formData.append("clientId", selectedClient?.id ?? "");
      formData.append("formLeadId", formLeadId ?? "");
      formData.append(
        "utilisateur",
        utilisateur
          ? `${utilisateur.name} ${utilisateur.lastName}`
          : "Utilisateur inconnu"
      );
      formData.append("nom", selectedClient?.nom ?? "");
      formData.append("titre", titre);
      formData.append("confidentialite", confidentialite);
      formData.append("noteDeBase", finalTranscript);
      formData.append("noteManuelle", manualText);
      formData.append("noteResume", summary);
      if (documentFile) {
        // Upload direct navigateur → Blob (contourne la limite 4,5 Mo),
        // avec une progression affichée pendant l'envoi.
        const url = await uploadFileToBlob(
          documentFile,
          "https://backend-myalfa.vercel.app/api/note-rdv/upload-token",
          ({ percentage }) => setUploadProgress(Math.round(percentage))
        );
        formData.append("documentUrl", url);
        formData.append("documentName", documentFile.name);
        formData.append(
          "documentSize",
          documentFile.size < 1024
            ? `${documentFile.size} o`
            : documentFile.size < 1024 * 1024
            ? `${Math.round(documentFile.size / 1024)} Ko`
            : `${(documentFile.size / (1024 * 1024)).toFixed(1)} Mo`
        );
        setUploadProgress(null);
      }

      const response = await authFetch("https://backend-myalfa.vercel.app/api/note-rdv",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Erreur API");

      dialog.alert("Note enregistrée avec succès !");
      setTitre("");
      setSummary("");
      setFinalTranscript("");
      setManualText("");
      setDocumentFile(null);
    } catch (error) {
      console.error("Erreur :", error);
      dialog.alert("Erreur lors de l'enregistrement.");
    } finally {
      setUploadProgress(null);
      setIsSaving(false);
    }
  };

  const isClientSelected = selectedClient?.id || formLeadId;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(summaryMail);
  };

  return (
    <div className="overflow-y-auto">
      <div className="p-2 space-y-3">
        {/* Client / Prospect */}
        <div className="space-y-1 relative">
          <label className="text-[11px] font-medium text-gray-700 flex items-center gap-1">
            <UserSearch size={12} /> Client / Prospect
          </label>

          <input
            type="text"
            value={searchClient}
            onChange={handleSearchClient}
            placeholder="Rechercher..."
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#840040] transition-all"
          />

          {clientSuggestions.length > 0 && (
            <ul className="absolute w-full bg-white border border-gray-200 rounded-md shadow max-h-32 overflow-y-auto z-10 text-xs">
              {clientSuggestions.map((client) => (
                <li
                  key={client.id}
                  onClick={() => selectClient(client)}
                  className="px-2 py-1.5 hover:bg-[#840040]/10 cursor-pointer transition"
                >
                  {client.nom}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Titre + Confidentialité */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* Titre */}
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-gray-700">
              Titre
            </label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex: RDV 2025"
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#840040] transition-all"
            />
          </div>

          {/* Confidentialité */}
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-gray-700">
              Confidentialité
            </label>
            <select
              value={confidentialite}
              onChange={(e) => setConfidentialite(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#840040] transition-all"
            >
              <option value="Public">Public</option>
              <option value="Associé">Associé</option>
              <option value="Privé">Privé</option>
            </select>
          </div>
        </div>

        {/* Document joint (optionnel) — une note de RDV peut etre un
            document deja redige (PDF, Word, Markdown...) plutot qu'une note
            texte/vocale, ou en plus de celle-ci. */}
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-gray-700 flex items-center gap-1">
            <Paperclip size={12} /> Document joint (PDF, Word, Markdown...
            optionnel)
          </label>

          {!documentFile ? (
            <label className="flex flex-col items-center justify-center w-full py-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#840040] hover:bg-[#840040]/5 transition-colors">
              <Upload size={18} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">
                Cliquez pour importer un document
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md"
                className="hidden"
                onChange={(e) => setDocumentFile(e.target.files[0] || null)}
              />
            </label>
          ) : (
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-md">
              <FileText size={16} className="text-[#840040] flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-xs text-gray-700">
                {documentFile.name}
              </span>
              <span className="text-[10px] text-gray-400 flex-shrink-0">
                {(documentFile.size / 1024).toFixed(1)} Ko
              </span>
              <button
                type="button"
                onClick={() => setDocumentFile(null)}
                className="text-gray-400 hover:text-red-600 transition flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Boutons */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <button
            onClick={toggleListening}
            disabled={!isClientSelected}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white transition-all
    ${
      !isClientSelected
        ? "bg-gray-400 cursor-not-allowed"
        : isListening
        ? "bg-[#840040] hover:scale-105"
        : "bg-gray-700 hover:scale-105"
    }`}
          >
            {isListening ? <MicOff size={14} /> : <Mic size={14} />}
            {isListening ? "Arrêter l'écoute" : "Démarrer l'écoute"}
          </button>

          <button
            onClick={clearText}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs transition"
          >
            <Trash2 size={14} /> Vider
          </button>

          <button
            onClick={summarizeText}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#840040] text-white text-xs hover:scale-105 transition"
          >
            <FileText size={14} /> Résumé
          </button>

          <button
            onClick={summarizeMail}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-gray-800 text-white text-xs hover:scale-105 transition"
          >
            <FileText size={14} /> Mail IA
          </button>
        </div>

        {/* Texte */}
        <div className="space-y-2">
          {" "}
          <label className="text-sm font-medium text-gray-700">
            {" "}
            Texte du rendez-vous{" "}
            <span className="text-xs text-gray-400 italic">
              {" "}
              ( par {utilisateur?.name || "Utilisateur"}{" "}
              {utilisateur?.lastName || "Utilisateur"} ){" "}
            </span>
          </label>{" "}
          <textarea
            value={finalTranscript}
            onChange={(e) => setFinalTranscript(e.target.value)}
            placeholder="La transcription apparaîtra ici..."
            className="w-full h-48 border border-gray-300 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-[#840040] resize-none"
          />{" "}
          {interimTranscript && (
            <p className="text-sm text-gray-400 italic">{interimTranscript}</p>
          )}{" "}
        </div>

        {/* Notes manuelles */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Notes manuelles
          </label>

          <div className="w-full ">
            <Editor
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder="Ajoutez vos notes prises pendant le rendez-vous..."
              className="w-full h-32 overflow-y-auto p-2"
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-[#840040]">
            {" "}
            <Loader2 className="animate-spin" size={18} /> Résumé en cours...{" "}
          </div>
        )}

        {/* Résumé */}
        {summary && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 w-full h-48 overflow-y-auto">
            <h3 className="font-semibold text-gray-700 mb-3">Résumé généré</h3>

            <div
              className="prose prose-sm max-w-none text-gray-700
prose-h3:text-[#840040]
prose-strong:text-gray-900
prose-li:marker:text-[#840040]"
            >
              <ReactMarkdown>{summary}</ReactMarkdown>
            </div>
          </div>
        )}

        {summaryMail && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 w-full h-64 overflow-y-auto relative">
            {/* Bouton copier */}
            <button
              onClick={copyToClipboard}
              className="absolute top-3 right-3 text-gray-500 hover:text-[#840040] transition"
              title="Copier"
            >
              <FileText size={16} />
            </button>

            <h3 className="font-semibold text-gray-700 mb-3">
              Mail de synthèse généré
            </h3>

            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown>{summaryMail}</ReactMarkdown>
            </div>
          </div>
        )}

        {isLoadingMail && (
          <div className="flex items-center gap-2 text-[#840040]">
            {" "}
            <Loader2 className="animate-spin" size={18} /> Mail IA...{" "}
          </div>
        )}

        <br />
        {/* Save */}
        <button
          onClick={enregistrerNote}
          disabled={!isClientSelected || isSaving}
          className=" w-full flex items-center justify-center gap-2 px-6 py-2 rounded-xl text-white font-semibold shadow-lg transition
          bg-[#840040] hover:scale-[1.02]
          disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              {uploadProgress !== null ? `Envoi du document... ${uploadProgress}%` : "Enregistrement en cours..."}
            </>
          ) : (
            <>
              <Save size={18} /> Enregistrer la note
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Rdv;
